import { omit } from 'lodash';
import type React from 'react';

import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3DataQuery,
  PAY_V2_QUERY,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import {
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import {
  type CompositionV3BreakdownDimension,
  getCompositionV3BreakdownOptions,
} from '../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  getCompositionV3Variables,
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { mergeDrilldownIntoCompositionV3Filters } from '../../../filters/utils/composition-v3/merge-drilldown-into-composition-v3-filters';
import { getMonthRefsFromCompositionV3Filters } from '../../../filters/utils/composition-v3/month-refs-from-v3-filters';
import { getV2Filters } from '../../../filters/utils/getV2Filters';
import { stripEntitiesFromFilters } from '../../../filters/utils/role-skill-ent-kind';
import { TOP_N_LIMIT } from '../constants';
import {
  getTopCompaniesFromPayV2,
  payV2EntitiesFromSelections,
} from '../TopNDataProvider';
import type { DataProviderQueryStatus } from '../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';
import { isOtherCompositionBucket } from './composition-v3/is-empty-composition-bucket';

type CompositionV3Payload = NonNullable<
  CompositionV3DataQuery['composition_v3']
>;

export type DrilldownFilter =
  | {
      segment: 'geography';
      level: 'region' | 'country' | 'msa' | 'city';
      id: number;
    }
  | {
      segment: 'role';
      levelId: 'role_k7' | 'role_k150' | 'role_k1500' | 'role_k15000';
      id: string;
    }
  | {
      segment: 'rics';
      level: 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company';
      id: number;
    }
  | {
      segment: 'skills';
      level: 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4';
      id: string;
    };

const SEGMENT_TO_V3_BREAKDOWN_DIMENSION: Record<
  DrilldownFilter['segment'],
  CompositionV3BreakdownDimension
> = {
  geography: 'geography',
  role: 'role',
  rics: 'rics',
  skills: 'skill',
};

function filterOutSkillsOtherBucket(
  data: CompositionV3Payload | undefined,
  drilldown: DrilldownFilter
): CompositionV3Payload | undefined {
  if (data === undefined || drilldown.segment !== 'skills') return data;
  return {
    ...data,
    entities: data.entities.map((entity) => ({
      ...entity,
      metrics: {
        ...entity.metrics,
        skills: entity.metrics.skills.filter(
          (skill) => !isOtherCompositionBucket(skill.id)
        ),
      },
    })),
  };
}

type Props = {
  primaryDimension: PrimaryFilterId;
  drilldown: DrilldownFilter;
  children: (result: {
    data: CompositionV3Payload | undefined;
    monthRefs: string[];
    status: DataProviderQueryStatus;
  }) => React.ReactNode;
};

export function CompositionTreemapDrilldownProvider({
  primaryDimension,
  drilldown,
  children,
}: Props) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { customSkillTree } = useCustomSkillTaxonomyTree();
  const { activeFilterMap, geographyMode } = useFilterContext();

  const baseVariablesV21 = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    geographyMode,
    customSkillTree,
    activeFilterMap,
  });

  const mergedFiltersV21 = mergeDrilldownIntoCompositionV3Filters(
    baseVariablesV21.filters,
    drilldown
  );

  const normalizedEffectiveForBreakdown =
    normalizeCompositionEffectiveFilters(effectiveFilters);

  const breakdownCustomSkillDeepestLevel =
    drilldown.segment === 'skills'
      ? drilldown.level
      : getCustomSkillDeepestLevelFromFilters(normalizedEffectiveForBreakdown);

  const breakdownOptionsV21 = getCompositionV3BreakdownOptions({
    compositionFilters: mergedFiltersV21,
    customSkillTree,
    customSkillDeepestLevel: breakdownCustomSkillDeepestLevel,
    includedBreakdownDimensions: [
      SEGMENT_TO_V3_BREAKDOWN_DIMENSION[drilldown.segment],
    ],
    geographyMode,
  });

  const drilldownVariablesV21 = {
    ...baseVariablesV21,
    filters: mergedFiltersV21,
    breakdown_options: breakdownOptionsV21,
  };

  const isRicsCompanyBreakdown =
    drilldown.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company');

  const primaryFilter = effectiveFilters.find((f) => f.isPrimary);
  const primaryEntities =
    primaryFilter?.type === FilterType.TREE
      ? payV2EntitiesFromSelections(primaryFilter.selections)
      : [];

  const topNFilters = isRicsCompanyBreakdown
    ? {
        ...getV2Filters({ primaryDimension, effectiveFilters }),
        rics_k400: [drilldown.id],
      }
    : null;

  const [{ data: topNData, status: topNStatus }] = useUrqlQueryWithStatus({
    query: PAY_V2_QUERY,
    variables: {
      req: {
        entities: primaryEntities,
        filters: topNFilters ?? { raw: false },
        ...(geographyMode === 'administrative' && {
          options: { geo_breakdown_state_over_msa: true },
        }),
      },
    },
    pause: !isRicsCompanyBreakdown || primaryEntities.length === 0,
  });

  const topEntities =
    isRicsCompanyBreakdown && topNData?.pay_v2
      ? getTopCompaniesFromPayV2(topNData.pay_v2)?.slice(0, TOP_N_LIMIT)
      : undefined;

  const compositionVariablesV21 = (() => {
    if (
      isRicsCompanyBreakdown &&
      topEntities != null &&
      topEntities.length > 0
    ) {
      const filtersWithoutRics = omit(mergedFiltersV21, [
        'rics_k10',
        'rics_k50',
        'rics_k400',
      ]);
      const entities = topEntities;
      return {
        ...drilldownVariablesV21,
        filters: stripEntitiesFromFilters(filtersWithoutRics, entities),
        entities,
      };
    }
    return drilldownVariablesV21;
  })();

  const compositionPause =
    isRicsCompanyBreakdown && (!topEntities || topEntities.length === 0);

  const [{ data: dataV21, status: compositionStatusV21 }] =
    useUrqlQueryWithStatus<CompositionV3DataQuery>({
      query: COMPOSITION_GET_DATA_V3,
      variables: compositionVariablesV21,
      pause: compositionPause,
    });

  const status = (() => {
    if (isRicsCompanyBreakdown) {
      return combineQueryStatus([topNStatus, compositionStatusV21]);
    }
    return compositionStatusV21;
  })();

  const monthRefs = getMonthRefsFromCompositionV3Filters(
    compositionVariablesV21.filters
  );

  const finalData = (() => {
    if (
      isRicsCompanyBreakdown &&
      (compositionPause ||
        (topNData != null && (!topEntities || topEntities.length === 0)))
    ) {
      return undefined;
    }
    const v21 = dataV21?.composition_v3;
    if (!v21) {
      return undefined;
    }
    return filterOutSkillsOtherBucket(v21, drilldown);
  })();

  return <>{children({ data: finalData, monthRefs, status })}</>;
}
